// src/admin/UserInventoryModal.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getUserInventoryById, purgeUserInventoryItems } from '../api'; // Adjust path if needed

// --- Material UI Imports ---
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText'; // For confirmation
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/system';
import { useSnackbar } from 'notistack';

// --- Constants ---
const ITEMS_PER_PAGE = 24; // Adjust as needed for the modal
const PLACEHOLDER_BG_COLOR = (theme) => alpha(theme.palette.background.paper, 0.5);
const STEAM_IMAGE_SIZE_SUFFIX = '/96fx96f?allow_animated=1';
const GRID_BORDER_COLOR = (theme) => alpha(theme.palette.divider, 0.4);

// --- Helper Functions (copied/adapted from InventoryPage.js) ---
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try { return new Date(dateString).toLocaleString(); }
    catch (e) { console.error("Error formatting date:", e); return 'Invalid Date'; }
};

const formatWear = (wearValue) => {
    if (wearValue === null || wearValue === undefined || wearValue === '') return 'N/A';
    if (typeof wearValue === 'number') return wearValue.toFixed(6);
    return String(wearValue);
};

const getSteamImageUrl = (iconUrlPath) => {
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            const separator = iconUrlPath.includes('?') ? '&' : '?';
            if (iconUrlPath.match(/\/\d+fx\d+f/) && !iconUrlPath.includes('allow_animated=1')) {
                return `${iconUrlPath}${separator}allow_animated=1`;
            }
            return `${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
        }
        return iconUrlPath;
    }
    if (iconUrlPath.startsWith('//')) return `https:${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
    if (iconUrlPath.startsWith('ttps://')) return `h${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${STEAM_IMAGE_SIZE_SUFFIX}`;
};

const getTooltipTitle = (item) => {
    if (!item) return '';
    const name = item.name || 'Unnamed Item';
    const date = formatDate(item.acquiredDate);
    const wear = formatWear(item.wear);
    return (
        <React.Fragment>
            <Typography color="inherit" sx={{ fontWeight: 'bold' }}>{name}</Typography>
            {wear !== 'N/A' && <Typography variant="caption" display="block">Wear: {wear}</Typography>}
            <Typography variant="caption" display="block">Acquired: {date}</Typography>
            <Typography variant="caption" display="block">Asset ID: {item.originalAssetId}</Typography>
            {item.isWithdrawn && <Typography variant="caption" display="block" sx={{ color: 'warning.light', fontWeight: 'bold' }}>Status: Withdrawn</Typography>}
        </React.Fragment>
    );
};
// --- End Helper Functions ---

function UserInventoryModal({ open, onClose, userId, userName }) {
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const { enqueueSnackbar } = useSnackbar();

    // --- NEW: State for Purge Action ---
    const [isPurging, setIsPurging] = useState(false);
    const [isConfirmPurgeOpen, setIsConfirmPurgeOpen] = useState(false);
    // --- END NEW ---

    const fetchInventory = useCallback(async (showLoading = true) => {
        if (!userId) return;
        if (showLoading) setLoading(true);
        setError(null);
        // setInventory([]); // Clear previous inventory - might cause flicker if not desired
        // setCurrentPage(1); // Reset page
        try {
            const data = await getUserInventoryById(userId);
            const sortedData = [...(data || [])].sort((a, b) =>
                new Date(b.acquiredDate) - new Date(a.acquiredDate)
            );
            setInventory(sortedData);
        } catch (err) {
            if (err.response && err.response.status === 404) {
                setError(`User ${userName || userId} not found or has no inventory.`);
                setInventory([]); // Ensure inventory is empty on 404
            } else {
                setError('Failed to load inventory. Please try again.');
            }
            console.error("User inventory fetch error:", err.response?.data || err.message || err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [userId, userName]); // Dependencies for fetchInventory

    useEffect(() => {
        if (open && userId) {
            setInventory([]); // Clear inventory immediately when modal opens for new user
            setCurrentPage(1); // Reset page
            fetchInventory(true); // Fetch with loading indicator
        } else if (!open) {
            // Clear state if modal is closed
            setInventory([]);
            setError(null);
            setLoading(false);
            setCurrentPage(1);
            setIsPurging(false);
            setIsConfirmPurgeOpen(false);
        }
    }, [open, userId, fetchInventory]); // Refetch when modal opens or user changes

    // --- NEW: Purge Handlers ---
    const handleOpenConfirmPurgeDialog = () => {
        setIsConfirmPurgeOpen(true);
    };

    const handleCloseConfirmPurgeDialog = () => {
        setIsConfirmPurgeOpen(false);
    };

    const handleConfirmPurge = async () => {
        if (!userId) return;
        setIsPurging(true);
        try {
            const result = await purgeUserInventoryItems(userId);
            enqueueSnackbar(result.message || 'Inventory purge processed.', {
                variant: result.success ? 'success' : 'info',
            });
            if (result.success) {
                await fetchInventory(false); // Refresh inventory without main loading spinner
            }
        } catch (error) {
            const errMsg = error.response?.data?.message || 'Failed to purge inventory.';
            enqueueSnackbar(errMsg, { variant: 'error' });
            console.error("Purge inventory error:", error.response || error);
        } finally {
            setIsPurging(false);
            setIsConfirmPurgeOpen(false);
        }
    };
    // --- END NEW ---

    // Pagination Logic
    const itemsOnPage = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return inventory.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [inventory, currentPage]);

    const pageCount = useMemo(() => {
        return Math.ceil(inventory.length / ITEMS_PER_PAGE);
    }, [inventory]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const canPurge = inventory.length > 0 && !inventory.every(item => item.isWithdrawn);

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Inventory for {userName || `User ID: ${userId}`} ({inventory.length} items)
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Loading inventory...</Typography>
                        </Box>
                    )}
                    {error && !loading && (
                        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
                    )}
                    {!loading && !error && inventory.length === 0 && (
                        <Alert severity="info" sx={{ m: 2, justifyContent: 'center' }}>
                            This user's inventory is empty.
                        </Alert>
                    )}
                    {!loading && !error && inventory.length > 0 && (
                        <>
                            {/* Grid container */}
                            <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                                {itemsOnPage.map((item) => (
                                    <Grid
                                        item
                                        xs={3} sm={2} md={1.5} lg={1.5} // Adjust grid sizing as needed for modal
                                        key={item.originalAssetId || item.userInventoryItemId}
                                        sx={{ border: theme => `1px solid ${GRID_BORDER_COLOR(theme)}`, boxSizing: 'border-box' }}
                                    >
                                        <Tooltip title={getTooltipTitle(item)} placement="top" arrow>
                                            <Card
                                                sx={(theme) => ({
                                                    width: '100%', aspectRatio: '1 / 1', display: 'flex', flexDirection: 'column',
                                                    alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
                                                    opacity: item.isWithdrawn ? 0.6 : 1,
                                                    border: item.isWithdrawn
                                                        ? `2px solid ${alpha('#FFA500', 0.8)}` // Orange border for withdrawn
                                                        : item.nameColor ? `2px solid #${item.nameColor}` : 'none',
                                                    cursor: 'default', // Not clickable in admin view
                                                    position: 'relative', backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                                    overflow: 'hidden', boxShadow: 1,
                                                })}
                                            >
                                                {item.isWithdrawn && (
                                                    <Chip label="W" title="Withdrawn" color="warning" size="small"
                                                        sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1, fontSize: '0.5rem', height: '14px', lineHeight: '14px', fontWeight: 'bold', '& .MuiChip-label': { px: '4px', py: '0px' }, opacity: 0.9 }} />
                                                )}
                                                <CardMedia component="img" sx={{ width: '80%', maxHeight: '80%', objectFit: 'contain' }}
                                                    image={getSteamImageUrl(item.iconUrl)} alt={item.name || 'Inventory item'}
                                                    onError={(e) => { e.target.style.display = 'none'; }} />
                                                {!item.iconUrl && (
                                                    <Box sx={{ width: '80%', height: '80%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                                        <Typography variant="h6" sx={{ fontSize: '2rem' }}>?</Typography>
                                                    </Box>
                                                )}
                                            </Card>
                                        </Tooltip>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Pagination Controls */}
                            {pageCount > 1 && (
                                <Stack spacing={2} sx={{ mt: 3, mb: 1, alignItems: 'center' }}>
                                    <Pagination
                                        count={pageCount}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="small" // Smaller pagination for modal
                                        showFirstButton
                                        showLastButton
                                        disabled={loading || isPurging}
                                    />
                                </Stack>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 1, justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleOpenConfirmPurgeDialog}
                        color="error"
                        variant="outlined"
                        disabled={loading || isPurging || !canPurge}
                        startIcon={isPurging ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isPurging ? 'Clearing...' : 'Clear User Inventory'}
                    </Button>
                    <Button onClick={onClose} disabled={isPurging}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* --- NEW: Confirmation Dialog for Purge --- */}
            <Dialog
                open={isConfirmPurgeOpen}
                onClose={handleCloseConfirmPurgeDialog}
                aria-labelledby="confirm-purge-dialog-title"
                aria-describedby="confirm-purge-dialog-description"
            >
                <DialogTitle id="confirm-purge-dialog-title">Confirm Inventory Clear</DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-purge-dialog-description">
                        Are you sure you want to clear all non-withdrawn items from this user's inventory?
                        This action will remove items from their site inventory and make them available again in the main item pool.
                        Items already marked as withdrawn by the user will NOT be affected.
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmPurgeDialog} color="inherit" disabled={isPurging}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmPurge} color="error" variant="contained" disabled={isPurging} startIcon={isPurging ? <CircularProgress size={20} color="inherit" /> : null}>
                        {isPurging ? 'Clearing...' : 'Confirm Clear'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* --- END NEW --- */}
        </>
    );
}

export default UserInventoryModal;
